import { FileTextOutlined, RightOutlined, TagsOutlined } from "@ant-design/icons";
import type React from "react";
import { eventEmitter } from "../../../utils/eventEmitter";

// A note Superdoc wrote, customized or regenerated, or its codes (orchestrator
// POC). The card is a pointer, not a preview — clicking it opens that note in
// the panel beside the chat (`openArtifact`, handled by the home page). Every
// version gets its own card, so the thread keeps them all.
export interface ArtifactData {
  audio_file_id: number;
  kind: "note" | "codes";
  title: string;
  version: number;
  status: "ready" | "deleted";
  code_count?: number;
}

export const OPEN_ARTIFACT_EVENT = "openArtifact";

export interface OpenArtifactPayload {
  audioFileId: number;
  kind: ArtifactData["kind"];
}

export const openArtifact = (data: Pick<ArtifactData, "audio_file_id" | "kind">) =>
  eventEmitter.emit(OPEN_ARTIFACT_EVENT, { audioFileId: data.audio_file_id, kind: data.kind } as OpenArtifactPayload);

const ArtifactCard: React.FC<{ data: ArtifactData }> = ({ data }) => {
  if (!data?.audio_file_id) return null;
  const isCodes = data.kind === "codes";
  const deleted = data.status === "deleted";

  // The template name as the clinician named it; the kind and version say
  // the rest.
  const title = isCodes ? `Codes${data.code_count != null ? ` · ${data.code_count}` : ""}` : data.title || "Note";
  const subtitle = deleted
    ? "Deleted"
    : [isCodes ? null : "Note", data.version > 1 ? `Version ${data.version}` : "Click to open"]
        .filter(Boolean)
        .join(" · ");

  return (
    <button
      type="button"
      disabled={deleted}
      onClick={() => openArtifact(data)}
      style={{ ...styles.card, ...(deleted ? styles.cardDeleted : {}) }}
    >
      <span style={{ ...styles.icon, ...(deleted ? styles.iconDeleted : {}) }}>
        {isCodes ? <TagsOutlined /> : <FileTextOutlined />}
      </span>
      <span style={styles.body}>
        <span style={{ ...styles.title, ...(deleted ? styles.titleDeleted : {}) }}>{title}</span>
        <span style={styles.subtitle}>{subtitle}</span>
      </span>
      {!deleted && <RightOutlined style={styles.chevron} />}
    </button>
  );
};

const styles: Record<string, React.CSSProperties> = {
  card: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    width: "100%",
    maxWidth: "320px",
    marginTop: "8px",
    padding: "10px 12px",
    borderRadius: "12px",
    border: "1px solid #E5E7EB",
    backgroundColor: "#FFFFFF",
    cursor: "pointer",
    fontFamily: "inherit",
    // Ancestors center-align text; every widget defends itself.
    textAlign: "left",
  },
  cardDeleted: { backgroundColor: "#F9FAFB", cursor: "not-allowed" },
  icon: {
    flexShrink: 0,
    width: "32px",
    height: "32px",
    borderRadius: "8px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#EEF2FF",
    color: "#4F46E5",
    fontSize: "16px",
  },
  iconDeleted: { backgroundColor: "#F3F4F6", color: "#9CA3AF" },
  body: { display: "flex", flexDirection: "column", minWidth: 0, flex: 1 },
  title: {
    fontSize: "13px",
    fontWeight: 600,
    color: "#111827",
    lineHeight: 1.4,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  titleDeleted: { color: "#9CA3AF", textDecoration: "line-through" },
  subtitle: { fontSize: "12px", color: "#6B7280", lineHeight: 1.4 },
  chevron: { flexShrink: 0, color: "#9CA3AF", fontSize: "11px" },
};

export default ArtifactCard;
